/**
 * Équipements & réservations — the shared vocabulary.
 * Spec: docs/superpowers/specs/2026-09-22-amenities-booking-design.md
 *
 * A template only seeds defaults and an icon; nothing branches on it.
 * Booking times are org-local wall clock: `date` (YYYY-MM-DD) plus
 * minutes since local midnight, never instants.
 */
import { z } from "zod/v4";

import type { Weekday } from "./weekday";
import { weekdaySchema } from "./weekday";

export const AMENITY_TEMPLATES = [
  "meeting_room",
  "coworking",
  "sport_court",
  "gym",
  "other",
] as const;
export type AmenityTemplate = (typeof AMENITY_TEMPLATES)[number];

export const AMENITY_MODES = ["range", "fixed_slots", "day_parts"] as const;
export type AmenityMode = (typeof AMENITY_MODES)[number];

export const AMENITY_STATUSES = ["active", "archived"] as const;
export type AmenityStatus = (typeof AMENITY_STATUSES)[number];

export const AMENITY_APPROVALS = ["none", "required"] as const;
export type AmenityApproval = (typeof AMENITY_APPROVALS)[number];

/** `unit_admin` joins this tuple when tenant-admin approval is built. */
export const AMENITY_APPROVER_SCOPES = ["org_admin"] as const;
export type AmenityApproverScope = (typeof AMENITY_APPROVER_SCOPES)[number];

export const AMENITY_ELIGIBILITIES = [
  "all_occupants",
  "selected_units",
] as const;
export type AmenityEligibility = (typeof AMENITY_ELIGIBILITIES)[number];

/** Unused in v1 — the credits hook. */
export const AMENITY_BILLING_UNITS = ["hour", "booking", "seat_day"] as const;
export type AmenityBillingUnit = (typeof AMENITY_BILLING_UNITS)[number];

/** No `completed` / `no_show`: completion is derived from time, and a state
 *  with no transition into it reads as a feature that exists. */
export const BOOKING_STATUSES = [
  "pending",
  "confirmed",
  "rejected",
  "cancelled",
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/** The statuses that hold capacity. */
export const LIVE_BOOKING_STATUSES = ["pending", "confirmed"] as const;

export const ORG_FEATURES = ["amenities"] as const;
export type OrgFeature = (typeof ORG_FEATURES)[number];

export const BOOKING_RULES = [
  "SLOT_TAKEN",
  "OUTSIDE_HOURS",
  "BAD_GRID",
  "TOO_SHORT",
  "TOO_LONG",
  "TOO_FAR_AHEAD",
  "PAST",
  "NOT_ELIGIBLE",
  "MAX_ACTIVE_REACHED",
  "CANCEL_DEADLINE_PASSED",
  "CAPACITY_CONFLICT",
  "FEATURE_DISABLED",
  "UNIT_REQUIRED",
] as const;
export type BookingRule = (typeof BOOKING_RULES)[number];

const RULE_MESSAGES: Record<BookingRule, string> = {
  SLOT_TAKEN: "Ce créneau vient d'être réservé. Choisissez-en un autre.",
  OUTSIDE_HOURS: "Ce créneau est en dehors des horaires d'ouverture.",
  BAD_GRID: "Ce créneau ne correspond pas aux horaires proposés.",
  TOO_SHORT: "La durée est inférieure au minimum autorisé.",
  TOO_LONG: "La durée dépasse le maximum autorisé.",
  TOO_FAR_AHEAD: "Cette date est trop éloignée pour être réservée.",
  PAST: "Ce créneau est déjà passé.",
  NOT_ELIGIBLE: "Cet équipement ne vous est pas ouvert.",
  MAX_ACTIVE_REACHED:
    "Vous avez atteint le nombre maximal de réservations à venir.",
  CANCEL_DEADLINE_PASSED: "Le délai d'annulation est dépassé.",
  CAPACITY_CONFLICT:
    "Des réservations à venir dépassent la nouvelle capacité. Annulez-les d'abord.",
  FEATURE_DISABLED:
    "Le module Réservations n'est pas activé pour cette organisation.",
  UNIT_REQUIRED:
    "Précisez à quel logement ou à quelle entreprise rattacher cette réservation.",
};

export function bookingRuleMessage(rule: BookingRule): string {
  return RULE_MESSAGES[rule];
}

const TEMPLATE_LABELS: Record<AmenityTemplate, string> = {
  meeting_room: "Salle de réunion",
  coworking: "Espace coworking",
  sport_court: "Terrain de sport",
  gym: "Salle de sport",
  other: "Autre équipement",
};

export function amenityTemplateLabel(t: AmenityTemplate): string {
  return TEMPLATE_LABELS[t];
}

const STATUS_LABELS: Record<BookingStatus, string> = {
  pending: "En attente",
  confirmed: "Confirmée",
  rejected: "Refusée",
  cancelled: "Annulée",
};

export function bookingStatusLabel(s: BookingStatus): string {
  return STATUS_LABELS[s];
}

export const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** A real calendar day: the shape alone lets 2026-13-40 or 2026-02-30
 *  through, which Date.UTC would silently roll into another day. */
export function isCalendarDateKey(key: string): boolean {
  if (!DATE_KEY_RE.test(key)) return false;
  const [y = NaN, m = NaN, d = NaN] = key.split("-").map(Number);
  const at = new Date(Date.UTC(y, m - 1, d));
  return (
    at.getUTCFullYear() === y &&
    at.getUTCMonth() === m - 1 &&
    at.getUTCDate() === d
  );
}

export const dateKeySchema = z
  .string()
  .regex(DATE_KEY_RE)
  .refine(isCalendarDateKey, { message: "Date invalide." });

/** Seats one booking may take on a pooled amenity. The web picker offers
 *  exactly this many; the server refuses more. */
export const MAX_SEATS_PER_BOOKING = 10;

const minuteOfDay = z.number().int().min(0).max(1440);

export const amenityHoursRowInput = z
  .object({
    weekday: weekdaySchema,
    openMinutes: minuteOfDay,
    closeMinutes: minuteOfDay,
  })
  .refine((h) => h.openMinutes < h.closeMinutes, {
    message: "L'heure de fermeture doit suivre l'heure d'ouverture.",
  });

export const amenityDayPartInput = z
  .object({
    label: z.string().trim().min(1).max(40),
    startMinutes: minuteOfDay,
    endMinutes: minuteOfDay,
  })
  .refine((p) => p.startMinutes < p.endMinutes, {
    message: "La fin doit suivre le début.",
  });

export const amenityUpsertInput = z
  .object({
    name: z.string().trim().min(1).max(80),
    template: z.enum(AMENITY_TEMPLATES),
    description: z.string().trim().max(500).nullish(),
    locationLabel: z.string().trim().max(120).nullish(),
    directions: z.string().trim().max(300).nullish(),
    mode: z.enum(AMENITY_MODES),
    stepMinutes: z.number().int().min(5).max(1440),
    slotMinutes: z.number().int().min(5).max(1440).nullable(),
    minMinutes: z.number().int().min(5).max(1440),
    maxMinutes: z.number().int().min(5).max(1440),
    bufferMinutes: z.number().int().min(0).max(240),
    maxAdvanceDays: z.number().int().min(0).max(365),
    cancelDeadlineMinutes: z.number().int().min(0).max(10080),
    maxActivePerUser: z.number().int().min(1).max(100).nullable(),
    approval: z.enum(AMENITY_APPROVALS),
    eligibility: z.enum(AMENITY_ELIGIBILITIES),
    capacity: z.number().int().min(1).max(1000),
    hours: z.array(amenityHoursRowInput).min(1).max(50),
    dayParts: z.array(amenityDayPartInput).max(10),
    eligibleUnitIds: z.array(z.string().min(1)).max(1000),
  })
  .superRefine((v, ctx) => {
    if (1440 % v.stepMinutes !== 0) {
      ctx.addIssue({
        code: "custom",
        path: ["stepMinutes"],
        message: "Le pas doit diviser la journée (15, 30, 60 min…).",
      });
    }
    if (v.minMinutes > v.maxMinutes) {
      ctx.addIssue({
        code: "custom",
        path: ["minMinutes"],
        message: "La durée minimale dépasse la durée maximale.",
      });
    }
    if (v.mode === "fixed_slots" && v.slotMinutes === null) {
      ctx.addIssue({
        code: "custom",
        path: ["slotMinutes"],
        message: "Indiquez la durée d'un créneau.",
      });
    }
    if (v.mode === "day_parts" && v.dayParts.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["dayParts"],
        message: "Ajoutez au moins une période (Matin, Après-midi…).",
      });
    }
    if (v.eligibility === "selected_units" && v.eligibleUnitIds.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["eligibleUnitIds"],
        message: "Choisissez au moins une unité.",
      });
    }
  });
export type AmenityUpsertInput = z.infer<typeof amenityUpsertInput>;

export const amenityIdInput = z.object({ id: z.string().min(1) });

export const amenityAvailabilityInput = z.object({
  amenityId: z.string().min(1),
  date: dateKeySchema,
});

const bookingTime = {
  date: dateKeySchema,
  startMinutes: minuteOfDay,
  endMinutes: minuteOfDay,
};

export const bookingCreateInput = z
  .object({
    amenityId: z.string().min(1),
    ...bookingTime,
    seats: z.number().int().min(1).max(MAX_SEATS_PER_BOOKING).default(1),
    attendeeCount: z.number().int().min(1).max(1000).nullish(),
    unitId: z.string().min(1).optional(),
  })
  .refine((b) => b.startMinutes < b.endMinutes, {
    message: "La fin doit suivre le début.",
  });
export type BookingCreateInput = z.infer<typeof bookingCreateInput>;

export const bookingCreateOnBehalfInput = z
  .object({
    amenityId: z.string().min(1),
    ...bookingTime,
    seats: z.number().int().min(1).max(MAX_SEATS_PER_BOOKING).default(1),
    attendeeCount: z.number().int().min(1).max(1000).nullish(),
    bookerUserId: z.string().min(1),
    unitId: z.string().min(1).nullable(),
  })
  .refine((b) => b.startMinutes < b.endMinutes, {
    message: "La fin doit suivre le début.",
  });

export const bookingIdInput = z.object({ id: z.string().min(1) });

export const bookingCancelInput = z.object({
  id: z.string().min(1),
  reason: z.string().trim().min(1).max(280).optional(),
  /** Revoke the visitor invites attached to this booking. */
  revokeInvites: z.boolean().default(true),
});

export const bookingRejectInput = z.object({
  id: z.string().min(1),
  reason: z.string().trim().min(1).max(280),
});

export const bookingListForAdminInput = z.object({
  amenityId: z.string().min(1).optional(),
  status: z.enum(BOOKING_STATUSES).optional(),
  from: dateKeySchema.optional(),
  to: dateKeySchema.optional(),
});

/** Gestion › Planning: one day, every équipement. */
export const bookingPlanningInput = z.object({ date: dateKeySchema });

export const featureSetInput = z.object({
  organizationId: z.string().min(1),
  feature: z.enum(ORG_FEATURES),
  enabled: z.boolean(),
});

export interface AmenityTemplateDefaults {
  mode: AmenityMode;
  stepMinutes: number;
  slotMinutes: number | null;
  minMinutes: number;
  maxMinutes: number;
  bufferMinutes: number;
  maxAdvanceDays: number;
  cancelDeadlineMinutes: number;
  maxActivePerUser: number | null;
  approval: AmenityApproval;
  capacity: number;
  hours: { weekday: Weekday; openMinutes: number; closeMinutes: number }[];
  dayParts: { label: string; startMinutes: number; endMinutes: number }[];
}

const WEEKDAYS_ONLY: Weekday[] = ["lun", "mar", "mer", "jeu", "ven"];
const ALL_DAYS: Weekday[] = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];
const hoursFor = (days: Weekday[], open: number, close: number) =>
  days.map((weekday) => ({ weekday, openMinutes: open, closeMinutes: close }));

export const AMENITY_TEMPLATE_DEFAULTS: Record<
  AmenityTemplate,
  AmenityTemplateDefaults
> = {
  meeting_room: {
    mode: "range",
    stepMinutes: 30,
    slotMinutes: null,
    minMinutes: 30,
    maxMinutes: 240,
    bufferMinutes: 0,
    maxAdvanceDays: 30,
    cancelDeadlineMinutes: 0,
    maxActivePerUser: null,
    approval: "none",
    capacity: 1,
    hours: hoursFor(WEEKDAYS_ONLY, 8 * 60, 19 * 60),
    dayParts: [],
  },
  coworking: {
    mode: "day_parts",
    stepMinutes: 60,
    slotMinutes: null,
    minMinutes: 60,
    maxMinutes: 1440,
    bufferMinutes: 0,
    maxAdvanceDays: 14,
    cancelDeadlineMinutes: 0,
    maxActivePerUser: null,
    approval: "none",
    capacity: 40,
    hours: hoursFor(WEEKDAYS_ONLY, 8 * 60, 19 * 60),
    dayParts: [
      { label: "Matin", startMinutes: 8 * 60, endMinutes: 13 * 60 },
      { label: "Après-midi", startMinutes: 13 * 60, endMinutes: 19 * 60 },
      { label: "Journée", startMinutes: 8 * 60, endMinutes: 19 * 60 },
    ],
  },
  sport_court: {
    mode: "fixed_slots",
    stepMinutes: 30,
    slotMinutes: 90,
    minMinutes: 90,
    maxMinutes: 90,
    bufferMinutes: 0,
    maxAdvanceDays: 7,
    cancelDeadlineMinutes: 120,
    maxActivePerUser: 2,
    approval: "none",
    capacity: 1,
    hours: hoursFor(ALL_DAYS, 8 * 60, 23 * 60),
    dayParts: [],
  },
  gym: {
    mode: "fixed_slots",
    stepMinutes: 60,
    slotMinutes: 60,
    minMinutes: 60,
    maxMinutes: 60,
    bufferMinutes: 0,
    maxAdvanceDays: 7,
    cancelDeadlineMinutes: 60,
    maxActivePerUser: 3,
    approval: "none",
    capacity: 10,
    hours: hoursFor(ALL_DAYS, 6 * 60, 22 * 60),
    dayParts: [],
  },
  other: {
    mode: "range",
    stepMinutes: 30,
    slotMinutes: null,
    minMinutes: 30,
    maxMinutes: 480,
    bufferMinutes: 0,
    maxAdvanceDays: 30,
    cancelDeadlineMinutes: 0,
    maxActivePerUser: null,
    approval: "required",
    capacity: 1,
    hours: hoursFor(WEEKDAYS_ONLY, 8 * 60, 19 * 60),
    dayParts: [],
  },
};
